<template>
  <NuxtLayout name="user">
    <div class="flex h-full justify-center sm:justify-start md:gap-[140px]">
      <ProfileMenu
        class="w-full md:!w-1/4 md:max-w-[300px]"
        :button-options="{ label: $t('user.account.titles.delete_account') }"
        :accordion-items="menuItems"
        :class="{ 'hidden md:!flex': $route.path !== paths.account.index }"
        @select="handleSelect"
        @delete="isDeleteAccountModalOpen = true"
      />

      <div
        class="flex flex-1"
        :class="{ 'hidden md:!block': $route.path === paths.account.index }"
      >
        <slot />
      </div>
    </div>

    <ChangeLangModal
      :is-open="isChangeLangModalOpen"
      :languages="langOptions"
      :current-lang="currentLocale.code"
      @close="isChangeLangModalOpen = false"
      @save="handleSelectLanguage"
    />

    <LogoutModal
      :is-open="isLogoutModalOpen"
      @keep="isLogoutModalOpen = false"
      @close="isLogoutModalOpen = false"
      @logout="
        logout();
        $router.replace(paths.home);
      "
    />

    <DeleteAccountModal
      v-model:isOpen="isDeleteAccountModalOpen"
      type="confirmation"
      :show-checkbox="true"
      @keep="isDeleteAccountModalOpen = false"
    />
  </NuxtLayout>
</template>

<script setup lang="ts">
import type {
  AccordionButtonOption,
  AccordionButtonProps,
} from "~/types/schemas/common/Molecule/Accordion";
import type { RadioProps } from "~/types/schemas/common/Atom/radio";
import paths from "~/constants/paths";

import ProfileMenu from "~/components/features/UserManagement/Organism/ProfileMenu.vue";
import LogoutModal from "~/components/features/UserManagement/Organism/Modals/LogoutModal.vue";
import DeleteAccountModal from "~/components/features/UserManagement/Organism/Modals/DeleteAccount/Modal.vue";
import ChangeLangModal from "~/components/features/UserManagement/Organism/Modals/ChangeLangModal.vue";
import useB2bUser from "~/composables/UserManagement/useB2bUser";
import useOrders from "~/composables/UserManagement/useOrders";

const isLogoutModalOpen = ref(false);
const isChangeLangModalOpen = ref(false);
const isDeleteAccountModalOpen = ref(false);

const { user, logout } = useB2bUser();
const { t } = useI18n();
const { availableLocales, currentLocale, setLang } = useLocale();
const { selectedOrder } = useOrders();
const router = useRouter();
const route = useRoute();
const { isDesktop } = useDevice();
const langOptions = computed<RadioProps[]>(() =>
  availableLocales.value.map((lang) => ({
    value: lang.code,
    label: lang.name,
  })),
);

const hasMultipleLanguages = computed(() => availableLocales.value.length > 1);

const menuItems = computed<AccordionButtonProps[]>(() => {
  const items: AccordionButtonProps[] = [
    {
      id: paths.account.orders.active,
      title: t("user.account.labels.orders"),
      titleIcon: "ic-bag",
      iconType: isDesktop ? "plus" : "arrow",
      isSelected:
        paths.account.orders.active === route.path ||
        paths.account.orders.inactive === route.path ||
        paths.account.orders.details === route.path,
      options: [
        {
          id: paths.account.orders.active,
          title: t("user.account.labels.active_orders"),
          isSelected:
            paths.account.orders.active === route.path ||
            (paths.account.orders.details === route.path &&
              !selectedOrder.value?.is_cancelled),
        },
        {
          id: paths.account.orders.inactive,
          title: t("user.orders.labels.orders_history"),
          isSelected:
            paths.account.orders.inactive === route.path ||
            (paths.account.orders.details === route.path &&
              selectedOrder.value?.is_cancelled),
        },
      ],
    },
    {
      id: paths.account.details,
      title:
        user.value?.user_type === "Individual"
          ? t("user.account.labels.account_details_tab")
          : t("user.b2b.auth.titles.company_profile"),
      titleIcon:
        user.value?.user_type === "Individual" ? "ic-user" : "ic-corporate",
      isSelected: paths.account.details === route.path,
    },
    {
      id: paths.account.addresses,
      title: t("user.account.labels.my_addresses_tab"),
      titleIcon: "ic-place",
      isSelected: paths.account.addresses === route.path,
    },
    {
      id: paths.account.paymentDetails,
      title: t("user.account.labels.payment_details_tab"),
      titleIcon: "ic-creditcard",
      isSelected: paths.account.paymentDetails === route.path,
    },
    {
      id: "logout",
      title: t("user.account.labels.logout_tab"),
      titleIcon: "ic-logout",
    },
  ];

  if (hasMultipleLanguages.value) {
    items.splice(4, 0, {
      id: "lang",
      title: t("user.account.labels.change_lang_tab"),
      titleIcon: "ic-world",
    });
  }

  return items;
});

const handleSelect = (option: AccordionButtonOption) => {
  if (option.id === "logout") {
    isLogoutModalOpen.value = true;
  } else if (option.id === "lang") {
    isChangeLangModalOpen.value = true;
  } else {
    router.push(option.id);
  }
};

const handleSelectLanguage = (langCode: string) => {
  if (langCode !== currentLocale.value.code) {
    const selectedLang = availableLocales.value.find(
      (item) => item.code === langCode,
    );

    if (selectedLang) setLang(selectedLang, true);
  }
};
</script>
